import { useRef, useState } from 'react';

/**
 * A form's working copy of a record the server owns, and the rules that keep
 * "neshranjene spremembe" honest about it.
 *
 * The server does not store bytes as typed: zod trims text and lowercases an
 * address, a number field is parsed, and Postgres puts the keys of a `jsonb`
 * object in its own order. Comparing the draft to the stored row byte for byte
 * therefore calls a saved form unsaved for good. Each field says instead when a
 * draft *means* what the server holds (`FieldSame`), and every other field is
 * compared as key-sorted JSON.
 */
export type FieldSame<T> = { [K in keyof T]?: (draft: T[K], server: T[K]) => boolean };

/** JSON with object keys sorted, so key order never counts as a difference. */
export function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : v,
  );
}

export const sameTrimmed = (a: string, b: string) => a.trim() === b.trim();

function sameField<T>(same: FieldSame<T>, key: keyof T, a: T[keyof T], b: T[keyof T]): boolean {
  const rule = same[key];
  return rule ? rule(a, b) : canonical(a) === canonical(b);
}

export function isDirty<T extends object>(draft: T, server: T, same: FieldSame<T>): boolean {
  return (Object.keys(server) as (keyof T)[]).some(
    (key) => !sameField(same, key, draft[key], server[key]),
  );
}

/**
 * Where the draft goes when the server's record moves from `base` to `next` —
 * after this form's own save, another coordinator's edit, or an import.
 *
 * A field takes the server's value when the coordinator has not touched it, or
 * when what they typed already means that value (the save that just landed,
 * trimmed); a field they did edit keeps their text. A nested object with no rule
 * of its own is merged the same way inside, so retargeting one post type does
 * not hold back a colleague's change to another. Returns `draft` itself when
 * nothing moves, so a caller can skip the state update.
 */
export function adoptServer<T extends object>(draft: T, base: T, next: T, same: FieldSame<T>): T {
  let out: T | null = null;
  for (const key of Object.keys(next) as (keyof T)[]) {
    let value: T[keyof T];
    if (!same[key] && isPlainObject(draft[key]) && isPlainObject(base[key]) && isPlainObject(next[key])) {
      value = adoptServer(draft[key], base[key], next[key], {});
    } else {
      const untouched = sameField(same, key, draft[key], base[key]);
      const meansNext = sameField(same, key, draft[key], next[key]);
      value = untouched || meansNext ? next[key] : draft[key];
    }
    if (canonical(value) !== canonical(draft[key])) {
      out ??= { ...draft };
      out[key] = value;
    }
  }
  return out ?? draft;
}

const isPlainObject = (v: unknown): v is object =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * The draft of a form over `server`, following the server as it changes.
 *
 * `server` may be rebuilt on every render; only a change in its content counts.
 */
export function useDraft<T extends object>(server: T, same: FieldSame<T>) {
  const [draft, setDraft] = useState(server);
  const [base, setBase] = useState(server);
  if (canonical(base) !== canonical(server)) {
    // Adjusting state during render: React re-runs this component before it
    // commits, so the draft never paints a frame behind the server.
    setBase(server);
    const adopted = adoptServer(draft, base, server, same);
    if (adopted !== draft) setDraft(adopted);
  }
  return {
    draft,
    setDraft,
    dirty: isDirty(draft, server, same),
    reset: () => setDraft(server),
  };
}

export type CommitState = 'idle' | 'saving' | 'error';

/**
 * A text field that writes itself when the coordinator leaves it.
 *
 * Controlled, so it follows the server: a value that arrives later — the
 * refetch after this very save, or a remount that first rendered from an older
 * cache — replaces the text unless the coordinator has typed over it. `save`
 * receives the trimmed text and resolves once the server has it; `false` from
 * `accept` refuses the text (an empty required name) and puts the stored value
 * back. A failed save keeps what was typed and says so, rather than dropping it.
 */
export function useCommitField({
  value,
  save,
  accept = () => true,
}: {
  value: string;
  save: (text: string) => Promise<unknown>;
  accept?: (text: string) => boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [base, setBase] = useState(value);
  const [state, setState] = useState<CommitState>('idle');
  // The newest commit, and what it sent: an older save landing late must not
  // settle the field, and while one is on its way that text is what the server
  // is about to hold — not `value`, which still shows the one before.
  const seq = useRef(0);
  const sent = useRef<string | null>(null);

  if (base !== value) {
    setBase(value);
    if (draft === base || draft.trim() === value) setDraft(value);
  }

  const commit = () => {
    const text = draft.trim();
    if (text === (sent.current ?? value)) {
      if (sent.current === null) {
        setDraft(value);
        if (state === 'error') setState('idle');
      }
      return;
    }
    if (!accept(text)) {
      // The stored value is back, so nothing here is unsaved any more.
      setDraft(value);
      if (sent.current === null && state === 'error') setState('idle');
      return;
    }
    const mine = (seq.current += 1);
    sent.current = text;
    setState('saving');
    save(text).then(
      () => {
        if (seq.current !== mine) return;
        sent.current = null;
        setState('idle');
        setDraft((d) => (d.trim() === text ? text : d));
      },
      () => {
        if (seq.current !== mine) return;
        sent.current = null;
        setState('error');
      },
    );
  };

  return {
    state,
    inputProps: {
      value: draft,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.currentTarget.value),
      onBlur: commit,
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      },
    },
  };
}
